import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useApiStatus from "../../../../../hooks/useApiStatus";
import OrderService from "../../../../../service/OrderService";
import { removelocalStorageToken } from "../../../../../utilities/sessionStorage/token";
import { removelocalStorageUsername } from "../../../../../utilities/sessionStorage/username";
import IospediscoService from "../../../../../service/iospedisco";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import AdminService from "../../../../../service/AdminService";

const useOrderDetails = ({
  admin = false,
  sales,
  onErrorLoadUserOrders,
  onErrorReceiveOrder,
  onSuccessReceiveOrder,
  onErrorSendOrder,
  onErrorFillOutAll,
  onSuccessSendOrder,
  onErrorDownloadProducts,
  onErrorGetParcel,
  onSuccessOrderIsReadyToPickup,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("orderId");

  const { isLoading, hasError, isSuccess, retryFetch, setIsLoading, setIsSuccess, setHasError } =
    useApiStatus({
      isLoading: true,
    });
  const [loadingLabel, setLoadingLabel] = useState(false);
  const [loadingPickup, setLoadingPickup] = useState(false);
  const [confirmReceivedVisible, setConfirmReceivedVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [links, setLinks] = useState([]);
  const [parcelInfo, setParcelInfo] = useState(null);
  const [orderId_, setOrderId_] = useState(null);
  const [confirmSendVisible, setConfirmSendVisible] = useState(false);

  const [currency, setCurrency] = useState("$");
  const [shippingCompany, setShippingCompany] = useState("");
  const [trackingObject, setTrackingObject] = useState("");
  const [isLoadingReadyForPickup, setIsLoadingReadyForPickup] = useState(false);

  const { showErrorToast } = useToastContext();

  const adminService = new AdminService();
  const orderService = new OrderService();
  const iospediscoService = new IospediscoService();

  const findOrderInTransactions = async (orderId) => {
    const token = localStorage.getItem("__extraconomy_token");
    if (!token || !orderId) return null;
    const res = await orderService.getAllTransactions(token, {
      page: 1,
      pageSize: 10,
      q: String(orderId),
    });
    if (res?.messageCode !== 200000 && res?.messageCode !== 200001) return null;
    const content = Array.isArray(res?.result?.content) ? res.result.content : [];
    return content.find((order) => order?.id === Number(orderId)) || null;
  };

  const loadUserOrders = async (cancel) => {
    try {
      setIsLoading(true);

      var res;
      if (admin) {
        res = await adminService.getAllOrders(
          localStorage.getItem("__extraconomy_token"),
          localStorage.getItem("__extraconomy_username")
        );
        if (cancel) return;
      } else {
        if (!sales) {
          res = await orderService.getPurchases(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_username")
          );
          if (cancel) return;
        } else {
          res = await orderService.getSales(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_username")
          );
          if (cancel) return;
        }
      }

      if (res.messageCode !== 200001 && res.messageCode !== 200000) {
        onErrorLoadUserOrders?.(res.messageCode);
        setIsLoading(false);
      } else {
        let order;
        if (admin) {
          order = res.result.find((order) => order.id === Number(id));
        } else {
          order = res.result.find((order) => order.status !== "PENDING" && order.id === Number(id));
        }

        if (!order) {
          const fallbackOrder = await findOrderInTransactions(id);
          if (!fallbackOrder) {
            onErrorLoadUserOrders?.(404);
            setCurrentOrder(null);
            setOrderId_(null);
            setIsLoading(false);
            return [];
          }
          order = fallbackOrder;
        }

        setCurrentOrder(order);
        setCurrency(order?.itens[0]?.product?.currency);
        setOrderId_(order.id);
        setIsLoading(false);
        return res.result;
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      console.log(e);
    }
  };

  async function receiveOrder(username, orderId) {
    try {
      setIsLoading(true);

      let res;

      if (!currentOrder.itens[0].product.downloadable)
        res = await orderService.postReceiveOrder(
          localStorage.getItem("__extraconomy_token"),
          username,
          orderId
        );
      else
        res = await orderService.getDownloadLinks(
          localStorage.getItem("__extraconomy_token"),
          username,
          orderId
        );

      if (res.messageCode !== 200000 && res.messageCode !== 200001) {
        onErrorReceiveOrder?.(res.messageCode);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setConfirmReceivedVisible(false);
        onSuccessReceiveOrder?.({ detail: "productReceivedSuccessfully" });

        await loadUserOrders(false);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      console.log(e);
    }
  }

  async function sendOrder(username, orderId, shippingCompany, trackingObject) {
    try {
      setIsLoading(true);

      if (
        !currentOrder?.itens[0]?.product?.subCategory?.service &&
        (shippingCompany.length === 0 || trackingObject.length === 0)
      ) {
        onErrorFillOutAll?.();
        setIsLoading(false);
      } else {
        const res = await orderService.postSendOrder(
          localStorage.getItem("__extraconomy_token"),
          username,
          orderId,
          currentOrder?.itens[0]?.product?.subCategory?.service ? "" : shippingCompany,
          currentOrder?.itens[0]?.product?.subCategory?.service ? "" : trackingObject
        );

        if (res.messageCode !== 200000) {
          onErrorSendOrder?.(res.messageCode);

          setIsLoading(false);
        } else {
          setIsLoading(false);
          setConfirmSendVisible(false);
          onSuccessSendOrder?.();

          const orders = await loadUserOrders(false);
          setCurrentOrder(orders.find((order) => order.id === currentOrder.id));
        }
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      console.log(e);
    }
  }

  async function downloadProducts(cancel) {
    try {
      setIsLoading(true);

      const res = await orderService.getDownloadLinks(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        currentOrder.id
      );
      if (cancel) return;

      if (res.messageCode !== 200001) {
        onErrorDownloadProducts?.(res.messageCode);

        if (res.messageCode === 500000) console.log(res);
        setIsLoading(false);
        return false;
      } else return res.result;
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      return false;
    }
  }

  useEffect(() => {
    let cancel = false;

    async function fetchProducts() {
      if (
        currentOrder &&
        currentOrder.status === "RECEIVED" &&
        currentOrder.itens[0].product.downloadable
      ) {
        setIsLoading(true);
        const products = await downloadProducts(cancel);
        if (cancel) return;

        if (products) {
          setLinks(products);
          setIsLoading(false);
        }
      }
    }

    fetchProducts();

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder]);

  const getParcel = async () => {
    try {
      if (
        currentOrder &&
        !currentOrder.itens[0].product.service &&
        !currentOrder.itens[0].product.downloadable
      ) {
        setIsLoading(true);

        const res = await iospediscoService.getTrackShipment({ orderid: currentOrder.id });

        if (!!res.trackingResponseError?.tracking_results) {
          setParcelInfo(null);
          onErrorGetParcel?.();
          setIsLoading(false);
          return;
        }

        setParcelInfo(res.trackingResponse.tracking_results[0]);
        setIsLoading(false);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoading(false);
      return false;
    }
  };

  const isAfterMarch272024 = (date) => {
    //27 de março de 2024 implantação de nova forma de frete
    const targetDate = new Date("2024-03-27T00:00:00");
    return date > targetDate;
  };

  // Verify pickup status
  useEffect(() => {
    if (
      currentOrder &&
      currentOrder?.shippingOption !== "FREE" &&
      !currentOrder?.pickinaddress &&
      isAfterMarch272024(new Date(currentOrder?.date))
    )
      getParcel(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder]);

  useEffect(() => {
    if (parcelInfo?.courier) setShippingCompany(parcelInfo?.courier);
    if (parcelInfo?.shipping_code) setTrackingObject(parcelInfo?.shipping_code);
  }, [parcelInfo]);

  async function downloadLabel() {
    try {
      setLoadingLabel(true);

      const res = await iospediscoService.getLabel({ orderId: currentOrder.id });

      const url = window.URL.createObjectURL(new Blob([res], { type: "application/pdf" }));
      var link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "label.pdf");
      document.body.appendChild(link);
      link.click();

      setLoadingLabel(false);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      showErrorToast({ detail: "errorDownloadLabel" });
      setIsLoading(false);
      setLoadingLabel(false);
      console.log(e);
    }
  }

  const isReadyToPickup = async () => {
    try {
      setIsLoadingReadyForPickup(true);

      const res = await orderService.postSendOrder(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        currentOrder?.id,
        null,
        null
      );

      if (res.messageCode !== 200000) {
        onErrorSendOrder?.(res.messageCode);
        setIsLoadingReadyForPickup(false);
      } else {
        setIsLoadingReadyForPickup(false);
        setConfirmSendVisible(false);
        onSuccessOrderIsReadyToPickup?.();

        await loadUserOrders(false);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        removelocalStorageToken();
        removelocalStorageUsername();
        window.location.replace("/login");
      }
      setIsLoadingReadyForPickup(false);
      console.log(e);
    }
  };

  useEffect(() => {
    let cancel = false;
    loadUserOrders(cancel);
    return () => (cancel = true); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isLoading,
    loadingLabel,
    loadingPickup,
    isSuccess,
    hasError,
    retryFetch,
    confirmReceivedVisible,
    links,
    currentOrder,
    parcelInfo,
    orderId_,
    confirmSendVisible,
    currency,
    shippingCompany,
    trackingObject,
    isLoadingReadyForPickup,
    setConfirmSendVisible,
    setOrderId_,
    setParcelInfo,
    setLinks,
    setCurrentOrder,
    setIsLoading,
    setLoadingLabel,
    setIsSuccess,
    setHasError,
    loadUserOrders,
    setLoadingPickup,
    setConfirmReceivedVisible,
    setCurrency,
    setShippingCompany,
    setTrackingObject,
    downloadLabel,
    sendOrder,
    receiveOrder,
    setIsLoadingReadyForPickup,
    isReadyToPickup,
  };
};

export default useOrderDetails;
